import {
    DestroyRef, Directive, OnInit, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subscription } from "rxjs";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatMenuTrigger } from "@angular/material/menu";
import { MatSelect } from "@angular/material/select";
import {
    MatDatepicker, MatDateRangePicker
} from "@angular/material/datepicker";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";

/**
 * Union of all Material trigger types that can be wired to this directive.
 */
export type Trigger =
    | MatAutocompleteTrigger
    | MatMenuTrigger
    | MatSelect
    | MatDatepicker<unknown>
    | MatDateRangePicker<unknown>;

interface TriggerAdapter {
    readonly isOpen: boolean;
    open(): void;
    close(): void;
}

/**
 * Host-only directive that participates a host element's open Material overlay
 * panel in the application's close-all signal.
 *
 * Self-injects `MatAutocompleteTrigger`, `MatMenuTrigger`, and `MatSelect` when
 * any of those are present on the host element. For datepicker panels, wire the
 * trigger manually via `setTrigger()` because the picker element is separate
 * from the input it is attached to.
 *
 * This directive is not applied via a selector. It is composed into components
 * or directives via the `hostDirectives` array. The host then injects the
 * directive instance with `inject(PanelTriggerHostDirective, { self: true })` to
 * call its methods or read its state.
 *
 * @example
 * ```typescript
 * @Component({
 *   selector: "mgd-my-component",
 *   hostDirectives: [PanelTriggerHostDirective],
 *   template: `<input [matAutocomplete]="auto">`
 * })
 * export class MyComponent {
 *   private readonly _panelTrigger = inject(PanelTriggerHostDirective, { self: true });
 * }
 * ```
 *
 * @selector none — composed via `hostDirectives` only
 * @exportAs mgdPanelTrigger
 */
@Directive({
    exportAs: "mgdPanelTrigger"
})
export class PanelTriggerHostDirective implements OnInit {
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _app = inject(COMMON_APP_SERVICE);

    private readonly _autocomplete = inject(MatAutocompleteTrigger, {
        self: true,
        optional: true
    });

    private readonly _menu = inject(MatMenuTrigger, {
        self: true,
        optional: true
    });

    private readonly _select = inject(MatSelect, {
        self: true,
        optional: true
    });

    private _adapter?: TriggerAdapter;
    private _closeAllSub?: Subscription;

    /** True when a trigger has been wired (auto-detected or via `setTrigger`). */
    get isTrigger(): boolean {return !!this._adapter;}

    /** True when the wired trigger's panel is currently open. */
    get isOpen(): boolean {return this._adapter?.isOpen ?? false;}

    ngOnInit(): void {
        const trigger = this._autocomplete ?? this._menu ?? this._select;
        if (trigger) {
            this.setTrigger(trigger);
        }
    }

    /** Opens the wired trigger's panel (no-op when no trigger is wired). */
    open(): void {this._adapter?.open();}

    /** Closes the wired trigger's panel (no-op when no trigger is wired). */
    close(): void {this._adapter?.close();}

    /**
     * Wires a Material trigger to this directive, replacing any prior trigger.
     *
     * @param trigger - The Material trigger to adopt.
     * @param ignoreCloseAll - When `true`, no `closeAllPanels$` subscription is
     * opened. Use this when another `PanelTriggerHostDirective` upstream is
     * already the close-all owner for the same trigger (e.g. the outer
     * host of `GenericAutocompleteMatControlComponent`).
     */
    setTrigger(trigger: Trigger, ignoreCloseAll: boolean = false): void {
        this._closeAllSub?.unsubscribe();
        this._adapter = this._toAdapter(trigger);

        if (!ignoreCloseAll) {
            this._closeAllSub = this._app.closeAllPanels$
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(() => this.close());
        }
    }

    private _toAdapter(trigger: Trigger): TriggerAdapter {
        if (trigger instanceof MatAutocompleteTrigger) {
            return {
                get isOpen() {return trigger.panelOpen;},
                open: () => trigger.openPanel(),
                close: () => trigger.closePanel()
            };
        }
        if (trigger instanceof MatMenuTrigger) {
            return {
                get isOpen() {return trigger.menuOpen;},
                open: () => trigger.openMenu(),
                close: () => trigger.closeMenu()
            };
        }
        if (trigger instanceof MatSelect) {
            return {
                get isOpen() {return trigger.panelOpen;},
                open: () => trigger.open(),
                close: () => trigger.close()
            };
        }
        // MatDatepicker / MatDateRangePicker (both extend MatDatepickerBase)
        return {
            get isOpen() {return (trigger as MatDatepicker<unknown> | MatDateRangePicker<unknown>).opened;},
            open: () => trigger.open(),
            close: () => trigger.close()
        };
    }
}
